/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useMutation } from "@apollo/client";
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  StackDivider,
  Text,
  VStack,
} from "@chakra-ui/react";
import coverPNG from "assets/pablo-street-art.png";
import { PasswordInput } from "components/ui";
import { useFormik } from "formik";
import {
  ResetUserPassword,
  ResetUserPasswordVariables,
} from "graphql/queries/types/ResetUserPassword";
import { RESET_USER_PASSWORD } from "graphql/queries/users.gql";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";

import styles from "./scss/Onboard.module.scss";

function validateEmail(value: string) {
  let error;
  if (!value) {
    error = "Required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
    error = "Invalid email address";
  }
  return { error };
}

const OnboardPage = () => {
  const { t } = useTranslation();
  const { token } = useParams();

  const [
    resetPassword,
    { data: resetPasswordData, loading: resetPasswordLoading, error: resetPasswordError },
  ] = useMutation<ResetUserPassword, ResetUserPasswordVariables>(RESET_USER_PASSWORD);

  const formikRecoverPassword = useFormik({
    enableReinitialize: true,
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    onSubmit: async ({ newPassword, confirmPassword }) => {
      if (!token) return;
      await resetPassword({
        variables: { newPassword, confirmPassword, resetPasswordToken: token },
      });
    },
  });

  if (!token) return <Navigate to="/" />;
  return (
    <div className={styles["with-sidebar"]}>
      <div className={styles.placeholder}>
        <img src={coverPNG} alt="" className={styles.placeholder__illustration} />
      </div>
      <div className={styles.login}>
        <div>
          <Stack spacing="4" divider={<StackDivider />}>
            <form id="requestPasswordResetToken" onSubmit={formikRecoverPassword.handleSubmit}>
              <VStack width="full" spacing="6" bgColor="white" p="8" align="flex-start">
                {resetPasswordError && (
                  <Alert status="error">
                    <>
                      <AlertIcon />
                      {resetPasswordError.message}
                    </>
                  </Alert>
                )}

                {resetPasswordData && (
                  <Alert status="info">
                    <VStack alignItems="flex-start">
                      <Text>{t("We've set your new password!")}</Text>
                      <Link to="/login">{t("You can now log in")}</Link>
                    </VStack>
                  </Alert>
                )}

                <Heading>{t("Welcome to speakart.io!")}</Heading>
                <Text align="left">{t("Set your password before logging in.")}</Text>

                <FormControl isRequired>
                  <FormLabel htmlFor="oldPassword">{t("New Password")}</FormLabel>
                  <PasswordInput
                    name="newPassword"
                    onChange={formikRecoverPassword.handleChange}
                    value={formikRecoverPassword.values.newPassword}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="oldPassword">{t("Confirm Password")}</FormLabel>
                  <PasswordInput
                    name="confirmPassword"
                    onChange={formikRecoverPassword.handleChange}
                    value={formikRecoverPassword.values.confirmPassword}
                  />
                </FormControl>

                <HStack width="full">
                  <Button
                    size="sm"
                    isLoading={resetPasswordLoading}
                    loadingText="Loading"
                    type="submit"
                  >
                    {t("Continue")}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </Stack>
        </div>
      </div>
    </div>
  );
};

export default OnboardPage;
